import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { computePosition, flip, shift } from '@floating-ui/react';

import './tooltip.scss';

export const TOOLTIP_CONTEXT = 'ap-tooltip';
export const POPPER_CONTEXT = 'ap-tooltip-popper';

export const TooltipContext = createContext(TOOLTIP_CONTEXT);
export const TooltipPopperContext = createContext(POPPER_CONTEXT);

const TooltipProvider = ({ children }) => {
  const [currentTitle, setCurrentTitle] = useState('test');
  return (
    <TooltipContext.Provider
      value={{
        currentTitle,
        setCurrentTitle,
      }}
    >
      {children}
    </TooltipContext.Provider>
  );
};

const PopperProvider = ({ children }) => {
  const [anchor, setAnchor] = useState();
  return (
    <TooltipPopperContext.Provider
      value={{
        anchor,
        setAnchor,
      }}
    >
      {children}
    </TooltipPopperContext.Provider>
  );
};

export const APTooltipProvider = ({ children, tooltipDelay, floatingProps }) => {

  return (
    <PopperProvider>
      <TooltipProvider>
        {children}
        <Tooltip
          tooltipDelay={tooltipDelay}
          floatingProps={floatingProps}
        />
      </TooltipProvider>
    </PopperProvider>
  );
};

export const Tooltip = ({ tooltipDelay = 1000, floatingProps }) => {
  const { currentTitle } = useContext(TooltipContext);
  const { anchor } = useContext(TooltipPopperContext);
  const { placement = 'bottom' } = floatingProps || {};
  const contentRef = useRef();
  const openTimerRef = useRef(0);

  const [showTooltip, setShowTooltip] = useState(false);

  const handleDelayedOpen = useCallback(() => {
    window.clearTimeout(openTimerRef.current);
    openTimerRef.current = window.setTimeout(() => {
      setShowTooltip(true);
    }, tooltipDelay);
  }, [showTooltip]);

  // Delayed show
  useEffect(() => {
    if (currentTitle && anchor) {
      handleDelayedOpen();
    } else {
      setShowTooltip(false);
      window.clearTimeout(openTimerRef.current);
    }
  }, [currentTitle, anchor]);

  useEffect(() => {
    if (contentRef.current && anchor) {
      const tooltip = contentRef.current;
      computePosition(anchor, tooltip, {
        placement,
        middleware: [flip(), shift()],
      }).then(({ x, y }) => {
        Object.assign(tooltip.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
      });
    }
  }, [showTooltip, anchor]);

  if (!showTooltip || !currentTitle || !anchor) return null;

  return (
    <div
      ref={contentRef}
      className="ap-tooltip"
    >
      {currentTitle}
    </div>
  );
};

export const useTooltip = () => {
  const { currentTitle, setCurrentTitle } = useContext(TooltipContext);
  const { anchor, setAnchor } = useContext(TooltipPopperContext);
  return {
    anchor,
    currentTitle,
    setCurrentTitle,
    setAnchor,
  };
};
